import { Skeleton } from "@patient-watch/ui/skeleton";
import { useSelectInputById } from "@/features/inputs/api/selectInputById";
import { cn } from "@/lib/utils";
import { BaseEntryValue } from "./BaseEntryValue";
import type { BaseEntryValueProps } from "./BaseEntryValue";

export interface ScaleRatingEntryValueProps extends Omit<BaseEntryValueProps, "value"> {
    value: number | null;
    textValue?: string | null;
    inputId: string;
    showValue?: boolean;
}

export function ScaleRatingEntryValue({
    value,
    textValue,
    inputLabel,
    inputId,
    showValue = true,
    size,
    ...props
}: ScaleRatingEntryValueProps) {
    const { data: input, isLoading } = useSelectInputById(inputId);

    if (isLoading) {
        return <Skeleton className={cn("w-24 h-5", size === "sm" && "w-20 h-4", size === "lg" && "w-28 h-6")} />;
    }

    // Staging sync replaces free text with this placeholder. Scale answers also store
    // the option label in text_value, so ignore it and use the questionnaire option.
    const labelToDisplay = textValue && textValue !== "[redacted for staging]" ? textValue : null;

    // Otherwise, fall back to looking it up from scale_options
    let fallbackLabel = null;
    if (!labelToDisplay && input?.scale_options && value !== null) {
        const scaleOptions =
            typeof input.scale_options === "string" ? JSON.parse(input.scale_options) : input.scale_options;

        const selectedOption = Array.isArray(scaleOptions)
            ? scaleOptions.find((option: { value: number; label: string }) => option.value === Number(value))
            : null;

        fallbackLabel = selectedOption?.label || null;
    }

    const displayLabel = labelToDisplay || fallbackLabel;

    if (value === null) {
        return (
            <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
                <span
                    className={cn(
                        "text-slate-900 dark:text-slate-200",
                        size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-md"
                    )}
                >
                    None
                </span>
            </BaseEntryValue>
        );
    }

    return (
        <BaseEntryValue inputLabel={inputLabel} size={size} {...props}>
            <div className="flex items-center gap-1">
                {showValue && value !== undefined && (
                    <span
                        className={cn(
                            "flex justify-center items-center mr-2 px-3 py-1 border border-slate-200 rounded-md aspect-square font-medium text-slate-900",
                            size === "sm" ? "text-md" : size === "lg" ? "text-xl" : "text-lg"
                        )}
                    >
                        {value}
                    </span>
                )}
                {displayLabel && (
                    <span
                        className={cn(
                            "text-slate-900 dark:text-slate-200",
                            size === "sm" ? "text-sm" : size === "lg" ? "text-lg" : "text-md"
                        )}
                    >
                        {displayLabel}
                    </span>
                )}
            </div>
        </BaseEntryValue>
    );
}
